import { useState } from "react";
import { motion } from "framer-motion";
import { ArrowRight } from "lucide-react";
import { Reveal } from "./Reveal";
import { useLead } from "@/context/LeadContext";

const AGENCY_COST_PER_LISTING = 850;
const SOLO_COST = 49;

export const RoiCalculator = () => {
  const [listings, setListings] = useState(6);
  const { openLead } = useLead();
  const agency = listings * AGENCY_COST_PER_LISTING;
  const savings = agency - SOLO_COST;
  const fmt = (n) => `$${n.toLocaleString()}`;

  return (
    <section id="roi" className="py-24 md:py-32 bg-ink text-bone" data-testid="roi-section">
      <div className="mx-auto max-w-[1100px] px-5 sm:px-8">
        <Reveal>
          <p className="text-xs uppercase tracking-[0.2em] text-coral mb-4">Do the math</p>
          <h2 className="font-serif text-4xl sm:text-5xl lg:text-6xl leading-[1.02] tracking-tight max-w-3xl">
            See how fast Chateaux pays for itself.
          </h2>
        </Reveal>

        <Reveal delay={0.1}>
          <div className="mt-14 grid grid-cols-1 lg:grid-cols-2 gap-12 lg:gap-16 items-center">
            <div>
              <label className="block text-sm text-bone/60 mb-4">
                You do <span className="text-bone font-medium">{listings}</span> listing{listings > 1 ? "s" : ""} / month
              </label>
              <input
                type="range"
                min="1"
                max="20"
                value={listings}
                onChange={(e) => setListings(Number(e.target.value))}
                data-testid="roi-slider"
                className="w-full accent-coral h-2 cursor-pointer"
                aria-label="Listings per month"
              />
              <div className="flex justify-between text-xs text-bone/40 mt-2">
                <span>1</span><span>10</span><span>20</span>
              </div>

              <button
                onClick={() => openLead({ source: "roi" })}
                data-testid="roi-cta"
                className="group mt-10 inline-flex items-center gap-2 bg-coral text-bone px-7 py-4 text-sm font-medium hover:bg-bone hover:text-ink transition-colors duration-300"
              >
                Start free — no credit card
                <ArrowRight className="w-4 h-4 transition-transform group-hover:translate-x-1" />
              </button>
            </div>

            <div className="space-y-5">
              <Row label="Traditional agency cost" value={`${fmt(agency)}/mo`} muted />
              <Row label="Chateaux Solo Agent" value={`${fmt(SOLO_COST)}/mo`} muted />
              <div className="border-t border-bone/15 pt-5">
                <p className="text-sm text-bone/60 mb-1">You save</p>
                <motion.p
                  key={savings}
                  initial={{ opacity: 0.4, y: 6 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.3 }}
                  className="font-serif text-6xl md:text-7xl text-coral leading-none"
                  data-testid="roi-savings"
                >
                  {fmt(savings)}<span className="text-2xl text-bone/50">/mo</span>
                </motion.p>
              </div>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
};

const Row = ({ label, value, muted }) => (
  <div className="flex items-baseline justify-between">
    <span className="text-sm text-bone/60">{label}</span>
    <span className={`font-serif text-2xl ${muted ? "text-bone/80" : "text-bone"}`}>{value}</span>
  </div>
);
